import React, { FC, useEffect, useMemo, useState } from 'react';
import TwDialog from '../TwDialog/TwDialog';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { EdiDelayReasonCode, delayReasonList } from './TwEdiDelayReasonList';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import { useSendEdiMessageUpdateMutation } from '../../redux/api/load/loadApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import {
  EdiStopMessageUpdate,
  ORDERFUL_SHIPMENT_STATUS_CODE
} from '../../redux/api/load/load.model';
import { STOP_TYPE, TripStop } from '../../redux/api/trips/trips.model';
import Box from '@mui/material/Box';
import TwDatePicker from '../TwDatePicker/TwDatePicker';
import TwTimePicker from '../TwTimePicker/TwTimePicker';
import useAuth from '../../providers/Auth/Auth.provider';
import DateUtils from '../../utils/dateUtils';

export enum TwEdiMessageType {
  DELAY_REASON = 'DELAY_REASON',
  CONFIRM_APPOINTMENT = 'CONFIRM_APPOINTMENT',
  SEND_CHECK_IN = 'SEND_CHECK_IN',
  SEND_CHECK_OUT = 'SEND_CHECK_OUT'
}

const modalTitleMap: Record<TwEdiMessageType, string> = {
  [TwEdiMessageType.DELAY_REASON]: 'Send delayed status - EDI 214 (SD)',
  [TwEdiMessageType.CONFIRM_APPOINTMENT]: 'Confirm appointment - EDI 214 (AA/AB)',
  [TwEdiMessageType.SEND_CHECK_IN]: 'Send check-in - EDI 214 (X3/X1)',
  [TwEdiMessageType.SEND_CHECK_OUT]: 'Send check-out - EDI 214 (AF/D1/CD)'
};

export interface TwEdiUpdateMessageProps {
  open: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  messageType: TwEdiMessageType;
  tripStop?: TripStop | null;
}

const TwEdiMessageUpdate: FC<TwEdiUpdateMessageProps> = ({
  open,
  onClose,
  onSuccess,
  messageType,
  tripStop
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const [sendEdiMessageUpdate] = useSendEdiMessageUpdateMutation();
  const [selectedReason, setSelectedReason] = useState<TwAutocompleteOption | null>(null);
  const [disableSave, setDisableSave] = useState(false);
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const [time, setTime] = useState<Date | undefined>(undefined);
  const [date, setDate] = useState<Date | undefined>(undefined);

  // useEffect on tripStop
  useEffect(() => {
    if (tripStop) {
      setTime(timeToShow());
      setDate(dateToShow());
    }
  }, [tripStop]);

  useEffect(() => {
    if (!open) {
      setSelectedReason(null);
      setDisableSave(false);
      setTime(undefined);
      setDate(undefined);
    }
  }, [open]);

  // Convert delayReasonList to TwAutocomplete optionList
  const delayReasonOptions = useMemo(() => {
    return delayReasonList.map((reason) => ({
      id: reason.delayCode,
      label: reason.label,
      value: reason.delayCode
    }));
  }, []);

  const modalTitle = modalTitleMap[messageType] || '';

  const timeToShow = (): Date | undefined => {
    switch (messageType) {
      case TwEdiMessageType.CONFIRM_APPOINTMENT:
        if (tripStop?.start_time) return DateUtils.convertTimeToDate(tripStop.start_time);
        return undefined;
      case TwEdiMessageType.SEND_CHECK_IN:
        return tripStop?.in_time ? new Date(tripStop?.in_time) : undefined;
      case TwEdiMessageType.SEND_CHECK_OUT:
        return tripStop?.out_time ? new Date(tripStop?.out_time) : undefined;
      default:
        return undefined;
    }
  };

  const dateToShow = (): Date | undefined => {
    switch (messageType) {
      case TwEdiMessageType.CONFIRM_APPOINTMENT:
        return tripStop?.start_date
          ? DateUtils.getDateInLocale(new Date(tripStop.start_date))
          : undefined;
      case TwEdiMessageType.SEND_CHECK_IN:
        return tripStop?.in_time ? new Date(tripStop?.in_time) : undefined;
      case TwEdiMessageType.SEND_CHECK_OUT:
        return tripStop?.out_time ? new Date(tripStop?.out_time) : undefined;
      default:
        return undefined;
    }
  };

  const getStatusCode = (): ORDERFUL_SHIPMENT_STATUS_CODE | undefined => {
    // if it's delay reason let's hard code to SD
    if (messageType === TwEdiMessageType.DELAY_REASON) {
      return 'SD';
    }

    // if it's check-in we will either return X3 or X1, depending on the stop type
    if (messageType === TwEdiMessageType.SEND_CHECK_IN) {
      return tripStop?.stop_type === STOP_TYPE.PICKUP ? 'X3' : 'X1';
    }

    // if it's check-out we will either return AF or CD, depending on the stop type
    if (messageType === TwEdiMessageType.SEND_CHECK_OUT) {
      return tripStop?.stop_type === STOP_TYPE.PICKUP ? 'AF' : 'CD';
    }

    return undefined;
  };

  const handleSubmit = (): void => {
    if (!tripStop || !tripStop.id) return;

    if (!selectedReason && messageType === TwEdiMessageType.DELAY_REASON) {
      addNotification({
        message: 'Please select a delay reason',
        variant: 'error'
      });
      return;
    }

    const ediStopMessageUpdate: EdiStopMessageUpdate = {
      status: getStatusCode(),
      reasonCode:
        messageType === TwEdiMessageType.DELAY_REASON
          ? (selectedReason?.value as EdiDelayReasonCode)
          : undefined,
      appointmentCode:
        messageType === TwEdiMessageType.CONFIRM_APPOINTMENT
          ? tripStop.stop_type === STOP_TYPE.PICKUP
            ? 'AA'
            : 'AB'
          : undefined,
      isCheckInOrOutUpdate:
        messageType === TwEdiMessageType.SEND_CHECK_IN ||
        messageType === TwEdiMessageType.SEND_CHECK_OUT
    };
    setDisableSave(true);

    sendEdiMessageUpdate({
      stopId: tripStop.id,
      ediStopMessageUpdate
    })
      .unwrap()
      .then(() => {
        addNotification({
          message: 'EDI status update sent',
          variant: 'success'
        });
        onSuccess && onSuccess();
      })
      .catch((err) => {
        console.error('Failed sending edi status update', err);
        addNotification({
          message: 'Failed to send EDI status update',
          variant: 'error'
        });
      })
      .finally(() => {
        setDisableSave(false);
        setSelectedReason(null);
        onClose && onClose();
      });
  };

  return (
    <TwDialog
      title={modalTitle}
      open={open}
      actions={[
        <TwButton
          key={0}
          label="Cancel"
          action={(): void => {
            onClose && onClose();
          }}
          type={TwButtonType.secondary}
        />,
        <TwButton
          key={1}
          label="Save"
          action={handleSubmit}
          disabled={disableSave}
          type={TwButtonType.primary}
        />
      ]}
      onClickClose={(): void => {
        onClose && onClose();
      }}
    >
      {messageType !== TwEdiMessageType.DELAY_REASON && (
        <Box flexDirection={'row'} className={'stop__date-time'}>
          <TwDatePicker
            name={`stop-date`}
            label={
              messageType === TwEdiMessageType.CONFIRM_APPOINTMENT
                ? 'Scheduled date'
                : 'Actual date'
            }
            containerClassName="stop__date"
            value={date}
            placeholder="MM/DD/YY"
            disabled={true}
            onChange={(date: unknown, keyboardInputValue?: string | undefined): void => {
              /* no action */
            }}
          />
          {!!time && (
            <Box className={'stop__time-container'} display={'initial'}>
              <>
                <TwTimePicker
                  name={`stop-time-from`}
                  label={`${
                    messageType === TwEdiMessageType.CONFIRM_APPOINTMENT
                      ? 'Scheduled time'
                      : 'Actual time'
                  } (${
                    messageType === TwEdiMessageType.CONFIRM_APPOINTMENT
                      ? tripStop?.location
                        ? tripStop?.location?.short_offset
                        : userData?.short_offset
                      : DateUtils.getBrowserTimeZoneOffsetNameShort()
                  })`}
                  value={time}
                  containerClassName={'stop__time-hide'}
                  ampm={!use24HourClock}
                  placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                  onChange={(date: unknown): void => {
                    /* no action */
                  }}
                  disabled={true}
                />
              </>
            </Box>
          )}
        </Box>
      )}

      {messageType === TwEdiMessageType.DELAY_REASON && (
        <TwAutocomplete
          label="Reason"
          optionList={delayReasonOptions}
          name="reason"
          value={selectedReason}
          placeholder="Select..."
          onChange={(_, reason): void => setSelectedReason(reason)}
        />
      )}
    </TwDialog>
  );
};

export default TwEdiMessageUpdate;
